import { Task } from '../types';

export const getCustomPriority = (task: Task): string | undefined => {
  // Look for Priority custom field
  if (task.custom_fields) {
    const priorityField = task.custom_fields.find(field => 
      field.name === 'Priority' || field.name === 'priority'
    );
    
    if (priorityField) {
      // Handle enum type (dropdown) priority field
      if (priorityField.type === 'enum' && priorityField.enum_value) {
        return priorityField.enum_value.name;
      }
      
      // Handle text type priority field
      if (priorityField.type === 'text' && priorityField.text_value) {
        return priorityField.text_value;
      }
      
      // Handle number type priority field (legacy support)
      if (priorityField.type === 'number' && priorityField.number_value) {
        const numValue = priorityField.number_value;
        if (numValue >= 1 && numValue <= 2) {
          return 'High';
        } else if (numValue >= 3 && numValue <= 4) {
          return 'Medium';
        } else if (numValue >= 5 && numValue <= 6) {
          return 'Low';
        }
      }
    }
  }
  
  return undefined;
};

export const getPrioritySortValue = (priority: string): number => {
  const priorityLower = priority.toLowerCase();
  
  // Parse priority type and level (e.g., "High-1", "Medium-5")
  const match = priorityLower.match(/^(high|medium|low)(?:-(\d+))?/);
  
  if (match) {
    const type = match[1];
    const level = match[2] ? parseInt(match[2]) : 1; // Default to 1 if no number
    
    // Calculate sort value: type base + level
    // High: 0-99, Medium: 100-199, Low: 200-299
    switch (type) {
      case 'high':
        return 0 + level; // High-1 = 1, High-2 = 2, etc.
      case 'medium':
        return 100 + level; // Medium-1 = 101, Medium-5 = 105, etc.
      case 'low':
        return 200 + level; // Low-1 = 201, Low-2 = 202, etc.
    }
  }
  
  // Handle other priority variations without numbers
  if (priorityLower.includes('high') || priorityLower.includes('urgent') || priorityLower.includes('critical')) {
    return 1;
  }
  
  if (priorityLower.includes('medium') || priorityLower.includes('normal') || priorityLower.includes('moderate')) {
    return 101;
  }
  
  if (priorityLower.includes('low') || priorityLower.includes('minor')) {
    return 201;
  }
  
  // Default to lowest priority for unknown values
  return 999;
};

export const getPriorityColorClass = (priority: string): string => {
  // Handle common priority naming patterns
  const priorityLower = priority.toLowerCase();
  
  // High priority variations
  if (priorityLower.includes('high') || priorityLower.includes('urgent') || priorityLower.includes('critical') || priorityLower.startsWith('high')) {
    return 'priority-high';
  }
  
  // Medium priority variations  
  if (priorityLower.includes('medium') || priorityLower.includes('normal') || priorityLower.includes('moderate') || priorityLower.startsWith('medium')) {
    return 'priority-medium';
  }
  
  // Low priority variations
  if (priorityLower.includes('low') || priorityLower.includes('minor') || priorityLower.startsWith('low')) {
    return 'priority-low';
  }
  
  // Default for unknown priorities
  return 'priority-none';
};

export const getDueDateClass = (dueDate: string): string => {
  const today = new Date();
  const due = new Date(dueDate);
  const diffTime = due.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) {
    return 'overdue';
  } else if (diffDays === 0) {
    return 'due-today';
  } else if (diffDays <= 3) {
    return 'due-soon';
  } else if (diffDays <= 7) {
    return 'due-this-week';
  } else {
    return 'due-later';
  }
};

export const getCustomPriorityValues = (tasks: Task[]): string[] => {
  const priorityValues = new Set<string>();
  
  tasks.forEach(task => {
    const priority = getCustomPriority(task);
    if (priority) {
      priorityValues.add(priority);
    }
  });
  
  // Sort priorities using the same logic as task sorting
  return Array.from(priorityValues).sort((a, b) => {
    const aValue = getPrioritySortValue(a);
    const bValue = getPrioritySortValue(b);
    return aValue - bValue;
  });
};

export const formatProjectNames = (projects: { gid: string; name: string }[] | undefined): string => {
  if (projects && projects.length > 0) {
    return projects.map(project => project.name).join(', ');
  }
  
  return 'No Project';
};

export const getTaskSections = (task: Task): string => {
  if (!task.memberships || task.memberships.length === 0) {
    return '';
  }
  
  const sections = task.memberships
    .map(membership => membership.section?.name)
    .filter(Boolean);
  
  return sections.join(', ');
};

export const getTaskTags = (taskId: string): string[] => {
  const stored = localStorage.getItem(`task_tags_${taskId}`);
  return stored ? JSON.parse(stored) : [];
};

export const setTaskTags = (taskId: string, tagNames: string[]) => {
  localStorage.setItem(`task_tags_${taskId}`, JSON.stringify(tagNames));
}; 